customElements.get("text-animation") ||
  customElements.define(
    "text-animation",
    class extends HTMLElement {
      constructor() {
        super();
        this.bannerHeading = this.querySelector(".reveal-text-animated-heading");
        if (!this.bannerHeading) {
          console.error("Element '.reveal-text-animated-heading' not found.");
          return;
        }
        this.sanitizeText = Array.from(this.bannerHeading.innerText);
        this.initPages();
        
        // Map one continuous metallic gradient across animated headline letters.
        // Every character receives the same full-headline gradient, translated
        // by its actual horizontal position. This preserves scroll reveal.
        if (this.bannerHeading.classList.contains("bpbd-reveal__heading")) {
          this.updateHeadlineGradient = this.updateHeadlineGradient.bind(this);
          this.queueHeadlineGradient = this.queueHeadlineGradient.bind(this);
          this.queueHeadlineGradient();
          window.addEventListener("resize", this.queueHeadlineGradient, { passive: true });
          if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(this.queueHeadlineGradient);
          }
          if (typeof ResizeObserver !== "undefined") {
            this.gradientObserver = new ResizeObserver(this.queueHeadlineGradient);
            this.gradientObserver.observe(this.bannerHeading);
          }
        }
        
        // Throttle scroll for better performance
        this.ticking = false;
        this.handleScroll = this.handleScroll.bind(this);
        
        window.addEventListener("scroll", () => {
          if (!this.ticking) {
            requestAnimationFrame(this.handleScroll);
            this.ticking = true;
          }
        });
        
        this.handleScroll();
      }
      
      initPages() {
        this.bannerHeading.innerText = ""; // Clear existing content
        const keepWordsTogether =
          this.bannerHeading.classList.contains("bpbd-reveal__heading") ||
          this.bannerHeading.classList.contains("bpbd-reveal__description");

        if (keepWordsTogether) {
          // Keep punctuation and its word together while retaining letter-by-letter reveal.
          const tokens = this.sanitizeText.join("").split(/(\s+)/);
          tokens.forEach((token) => {
            if (!token) return;
            if (/^\s+$/.test(token)) {
              this.bannerHeading.appendChild(document.createTextNode(" "));
              return;
            }
            const word = document.createElement("span");
            word.className = "text-animation__word";
            Array.from(token).forEach((char) => {
              const letter = document.createElement("span");
              letter.className = "text-animation__char";
              letter.textContent = char;
              word.appendChild(letter);
            });
            this.bannerHeading.appendChild(word);
          });
          return;
        }

        this.sanitizeText.forEach((char) => {
          if (char === " ") {
            this.bannerHeading.appendChild(document.createTextNode(" "));
          } else {
            const span = document.createElement("span");
            span.className = "text-animation__char";
            span.textContent = char;
            this.bannerHeading.appendChild(span);
          }
        });
      }
      
      queueHeadlineGradient() {
        if (this.gradientFrame) return;
        this.gradientFrame = requestAnimationFrame(() => {
          this.gradientFrame = null;
          this.updateHeadlineGradient();
        });
      }

      updateHeadlineGradient() {
        if (!this.bannerHeading || !this.bannerHeading.isConnected) return;
        const headingRect = this.bannerHeading.getBoundingClientRect();
        const width = Math.max(1, Math.ceil(headingRect.width));
        this.bannerHeading.querySelectorAll(".text-animation__char").forEach((letter) => {
          const letterRect = letter.getBoundingClientRect();
          letter.style.backgroundSize = width + "px 100%";
          letter.style.backgroundPosition = Math.round(headingRect.left - letterRect.left) + "px center";
        });
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this.queueHeadlineGradient);
        if (this.gradientObserver) this.gradientObserver.disconnect();
        if (this.gradientFrame) cancelAnimationFrame(this.gradientFrame);
      }

      handleScroll() {
        this.ticking = false;
        const spans = this.bannerHeading.querySelectorAll(".text-animation__char");
        
        // Check if we're near the bottom of the page (100-200px remaining)
        const scrollPosition = window.scrollY;
        const documentHeight = document.documentElement.scrollHeight;
        const windowHeight = window.innerHeight;
        const distanceFromBottom = documentHeight - (scrollPosition + windowHeight);
        const isNearBottom = distanceFromBottom <= 200 && distanceFromBottom >= 0;
        
        spans.forEach((char) => {
          const rect = char.getBoundingClientRect();
          const topOffset = rect.top - window.innerHeight * 0.5;
          
          // Your exact original calculations for top-to-bottom scrolling
          let opacityValue = Math.max(
            0,
            Math.min(1, 1 - (topOffset * 0.01 + rect.left * 0.001))
          );
          let translateY = Math.max(
            0,
            Math.min(1, 1 - (topOffset * 0.01 + rect.left * 0.001))
          );
          
          // Additional bottom-to-top animation when near page bottom
          if (isNearBottom && distanceFromBottom <= 150) {
            const bottomProgress = Math.max(0, Math.min(1, (150 - distanceFromBottom) / 150));
            
            // Enhance the animation when scrolling up near bottom
            opacityValue = Math.max(opacityValue, bottomProgress);
            translateY = Math.min(translateY, 1 - bottomProgress);
          }
          
          // Apply styles for animation - exactly as your original
          char.style.opacity = opacityValue.toFixed(4);
          char.style.display = "inline-block";
          char.style.position = "relative";
          char.style.transform = `translate3d(0px, ${translateY.toFixed(4)}px, 0px)`;
        });
      }
    }
  );